import { useContext, useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import EndpointsEnum from "../../../../models/EndpointsEnum";
import IDeleteWebhookDTO from "../../../../models/DTO/IDeleteWebhookDTO";
import IOnDeleteWebhookDTO from "../../../../models/DTO/IOnDeleteWebhookDTO";
import IOnSendTestWebhookDTO from "../../../../models/DTO/IOnSendTestWebhookDTO";
import ISendTestWebhookDTO from "../../../../models/DTO/ISendTestWebhookDTO";
import INotification from "../../../../models/INotification";
import IWebhookSummary from "../../../../models/IWebhookSummary";
import NotificationLevelEnum from "../../../../models/NotificationLevelEnum";
import NotificationPositionEnum from "../../../../models/NotificationPositionEnum";
import WebhookDeliveryStatusEnum from "../../../../models/WebhookDeliveryStatusEnum";
import OwButton from "../../../shared/OwButton/OwButton";
import OwLabel from "../../../shared/OwLabel/OwLabel";
import { WebhookSidebar } from "../WebhookSidebar/WebhookSidebar";
import { WebhookDeliveries } from "../WebhookDeliveries/WebhookDeliveries";
import { useWebhookEndpoints } from "./useWebhookEndpoints";
import { WEBHOOK_DOCS_URL, webhookHealth } from "./webhookEvents";
import "./Webhooks.scss";
import UserContext from "../../../../contexts/UserContext";
import SupplierContext from "../../../../contexts/SupplierContext";
import { SocketContext } from "../../../../contexts/SocketContext";
import UiContext from "../../../../contexts/UiContext";

/** How long a delete or test send waits for the server before the row lets the user try again. */
const REQUEST_TIMEOUT_MS = 15000;

type TestResult = { status: "sent" | "failed"; httpStatus: number | null; error: string | null };

/**
 * Business Settings > Webhooks: the business's endpoints with their health, a test send, the
 * delivery log, and the sidebar to add or edit one.
 */
export const Webhooks = () => {
    const intl = useIntl();
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const socket = useContext(SocketContext)!;
    const uiContext = useContext(UiContext);

    const { webhooks, maxWebhooks, loaded, reload } = useWebhookEndpoints(true);

    const [confirmingDelete, setConfirmingDelete] = useState<string | null>(null);
    const [testing, setTesting] = useState<string | null>(null);
    const [testResults, setTestResults] = useState<Record<string, TestResult>>({});
    const confirmTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const pendingDelete = useRef<((data: IOnDeleteWebhookDTO) => void) | null>(null);
    const deleteTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const testTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const t = (id: string, values?: Record<string, any>) => intl.formatMessage({ id, defaultMessage: `_${id}_` }, values);

    const base = () => ({ userGuid: userModel.guid, userToken: userModel.token, supplierGuid: supplierModel.guid });

    const onSendTest = (data: IOnSendTestWebhookDTO) => {
        const delivery = data.delivery;
        const succeeded = delivery.status === WebhookDeliveryStatusEnum.SUCCEEDED;

        setTesting(null);

        if (testTimer.current) {
            clearTimeout(testTimer.current);
            testTimer.current = null;
        }

        setTestResults((previous) => ({
            ...previous,
            [delivery.webhook_guid]: {
                status: succeeded ? "sent" : "failed",
                httpStatus: delivery.last_http_status,
                error: delivery.last_error,
            },
        }));
        reload();
    };

    useEffect(() => {
        socket.on(EndpointsEnum.WebhooksOnSendTest, onSendTest);

        return () => {
            socket.off(EndpointsEnum.WebhooksOnSendTest, onSendTest);

            if (confirmTimer.current) {
                clearTimeout(confirmTimer.current);
            }

            if (testTimer.current) {
                clearTimeout(testTimer.current);
            }

            clearPendingDelete();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const clearPendingDelete = () => {
        if (pendingDelete.current) {
            socket.off(EndpointsEnum.WebhooksOnDeleteWebhook, pendingDelete.current);
            pendingDelete.current = null;
        }

        if (deleteTimer.current) {
            clearTimeout(deleteTimer.current);
            deleteTimer.current = null;
        }
    };

    const closeSidebar = (refresh?: boolean) => {
        uiContext.setSidebarComponent(null);
        uiContext.toggleSidebar(false);

        if (refresh !== false) {
            reload();
        }
    };

    const openEditor = (webhook?: IWebhookSummary) => {
        uiContext.setSidebarComponent(<WebhookSidebar cancelFn={closeSidebar} webhook={webhook} />);
        uiContext.toggleSidebar(true);
    };

    const openDeliveries = (webhook: IWebhookSummary) => {
        uiContext.setSidebarComponent(<WebhookDeliveries webhook={webhook} cancelFn={() => closeSidebar(false)} />);
        uiContext.toggleSidebar(true);
    };

    const sendTest = (webhook: IWebhookSummary) => {
        if (testing) {
            return;
        }

        const payload: ISendTestWebhookDTO = { ...base(), guid: webhook.guid };

        setTesting(webhook.guid);
        setTestResults((previous) => {
            const next = { ...previous };

            delete next[webhook.guid];

            return next;
        });
        socket.emit(EndpointsEnum.WebhooksSendTest, payload);
        testTimer.current = setTimeout(() => setTesting(null), REQUEST_TIMEOUT_MS);
    };

    const onDeleted = () => {
        clearPendingDelete();

        const notification: INotification = {
            message: "successfully_deleted_webhook",
            position: NotificationPositionEnum.bc,
            level: NotificationLevelEnum.success,
        };

        uiContext.notifyFn(null, notification);
        reload();
    };

    const remove = (webhook: IWebhookSummary) => {
        if (pendingDelete.current) {
            return;
        }

        if (confirmingDelete !== webhook.guid) {
            setConfirmingDelete(webhook.guid);

            if (confirmTimer.current) {
                clearTimeout(confirmTimer.current);
            }

            confirmTimer.current = setTimeout(() => setConfirmingDelete(null), 4000);

            return;
        }

        setConfirmingDelete(null);

        const payload: IDeleteWebhookDTO = { ...base(), guid: webhook.guid };

        pendingDelete.current = onDeleted;
        socket.once(EndpointsEnum.WebhooksOnDeleteWebhook, onDeleted);
        socket.emit(EndpointsEnum.WebhooksDeleteWebhook, payload);
        deleteTimer.current = setTimeout(clearPendingDelete, REQUEST_TIMEOUT_MS);
    };

    const atCap = webhooks.length >= maxWebhooks;
    const countLabel = webhooks.length === 1 ? t("one_webhook") : t("webhooks_count", { count: webhooks.length });

    return (
        <div className={"Webhooks"} data-testid={"webhooks"}>
            <div className={"Webhooks-bar"}>
                {loaded ? (
                    <span className={"SettingsChip" + (webhooks.length ? " on" : "")} data-testid={"webhooks-count"}>
                        <i className={"fad fa-bolt"} aria-hidden={true} />
                        {atCap ? t("webhooks_limit", { count: webhooks.length, max: maxWebhooks }) : countLabel}
                    </span>
                ) : null}
                <a className={"Webhooks-docs"} href={WEBHOOK_DOCS_URL} target={"_blank"} rel={"noreferrer"}>
                    <i className={"fad fa-external-link-alt"} aria-hidden={true} />
                    <OwLabel className={"pointer"} title={"view_webhook_docs"} noBottomMargin={true} />
                </a>
                <span className={"Webhooks-spacer"} />
                <OwButton
                    title={"create_webhook"}
                    leftIconClass={"fas fa-plus"}
                    fn={() => openEditor()}
                    disabled={atCap}
                    disableWidthOnClick={true}
                    testId={"webhooks-create"}
                />
            </div>

            <div className={"Webhooks-list"} data-testid={"webhooks-list"}>
                {loaded && !webhooks.length ? (
                    <div className={"Webhooks-empty"} data-testid={"webhooks-empty"}>
                        <i className={"fad fa-bolt"} aria-hidden={true} />
                        <div>
                            <div className={"Webhooks-empty-title"}>
                                <OwLabel title={"no_webhooks_yet"} noBottomMargin={true} />
                            </div>
                            <div className={"Webhooks-empty-help"}>
                                <OwLabel title={"help_text_no_webhooks"} noBottomMargin={true} />
                            </div>
                        </div>
                    </div>
                ) : null}

                {webhooks.map((webhook) => {
                    const health = webhookHealth(webhook);
                    const confirming = confirmingDelete === webhook.guid;
                    const result = testResults[webhook.guid];
                    const isTesting = testing === webhook.guid;

                    return (
                        <div className={`Webhooks-row ${health}`} key={webhook.guid} data-testid={`webhook-${webhook.guid}`}>
                            <span className={"Webhooks-icon"}>
                                <i className={"fad fa-bolt"} aria-hidden={true} />
                            </span>
                            <div className={"Webhooks-main"}>
                                <div className={"Webhooks-name"}>
                                    {webhook.name}
                                    <span className={`Webhooks-status ${health}`} data-testid={`webhook-status-${webhook.guid}`}>
                                        {t(`webhook_status_${health}`)}
                                    </span>
                                </div>
                                <div className={"Webhooks-meta"}>
                                    <code className={"Webhooks-url"}>{webhook.url}</code>
                                </div>
                                <div className={"Webhooks-meta"}>
                                    <span>{t("webhook_events_count", { count: webhook.events.length })}</span>
                                    <span>{t("webhook_triggers_count", { count: webhook.workflow_triggers.length })}</span>
                                    <span data-testid={`webhook-last-${webhook.guid}`}>
                                        {webhook.last_success_at
                                            ? t("webhook_last_success", {
                                                  date: intl.formatDate(webhook.last_success_at, {
                                                      dateStyle: "medium",
                                                      timeStyle: "short",
                                                  }),
                                              })
                                            : t("webhook_never_delivered")}
                                    </span>
                                </div>
                                {health === "disabled" ? (
                                    <div className={"Webhooks-warning"} data-testid={`webhook-disabled-${webhook.guid}`}>
                                        <i className={"fad fa-exclamation-triangle"} aria-hidden={true} />
                                        {t("webhook_disabled_reason", { reason: webhook.disabled_reason || "" })}
                                    </div>
                                ) : null}
                                {result ? (
                                    <div
                                        className={`Webhooks-test ${result.status === "sent" ? "ok" : "bad"}`}
                                        data-testid={`webhook-test-result-${webhook.guid}`}
                                    >
                                        <i className={result.status === "sent" ? "fas fa-check" : "fas fa-times"} aria-hidden={true} />
                                        {result.status === "sent"
                                            ? t("webhook_test_delivered", { status: result.httpStatus ?? "" })
                                            : t("webhook_test_failed", { error: result.error || result.httpStatus || "" })}
                                    </div>
                                ) : null}
                            </div>
                            <div className={"Webhooks-actions"}>
                                {confirming ? (
                                    <>
                                        <OwButton
                                            title={"cancel"}
                                            className={"btn-link text-muted btn-sm"}
                                            fn={() => setConfirmingDelete(null)}
                                            disableWidthOnClick={true}
                                            disableDblClickPrevention={true}
                                            testId={`webhook-cancel-delete-${webhook.guid}`}
                                        />
                                        <OwButton
                                            title={"confirm_delete"}
                                            className={"btn-danger btn-sm"}
                                            leftIconClass={"fad fa-trash-alt"}
                                            fn={() => remove(webhook)}
                                            disableWidthOnClick={true}
                                            testId={`webhook-confirm-delete-${webhook.guid}`}
                                        />
                                    </>
                                ) : (
                                    <>
                                        <OwButton
                                            title={"send_test"}
                                            className={"btn-link text-muted btn-sm"}
                                            leftIconClass={isTesting ? "fas fa-spinner fa-spin" : "fad fa-paper-plane"}
                                            fn={() => sendTest(webhook)}
                                            disabled={!!testing || health !== "active"}
                                            disableWidthOnClick={true}
                                            disableDblClickPrevention={true}
                                            testId={`webhook-test-${webhook.guid}`}
                                        />
                                        <OwButton
                                            title={"deliveries"}
                                            className={"btn-link text-muted btn-sm"}
                                            leftIconClass={"fad fa-list-timeline"}
                                            fn={() => openDeliveries(webhook)}
                                            disableWidthOnClick={true}
                                            testId={`webhook-deliveries-${webhook.guid}`}
                                        />
                                        <OwButton
                                            centerIconClass={"fad fa-pen-to-square"}
                                            className={"btn-link text-muted"}
                                            ariaLabel={`${t("edit")} ${webhook.name}`}
                                            hoverTitle={t("edit")}
                                            fn={() => openEditor(webhook)}
                                            disableWidthOnClick={true}
                                            testId={`webhook-edit-${webhook.guid}`}
                                        />
                                        <OwButton
                                            centerIconClass={"fad fa-trash-alt"}
                                            className={"btn-link text-muted"}
                                            ariaLabel={`${t("delete")} ${webhook.name}`}
                                            hoverTitle={t("delete")}
                                            fn={() => remove(webhook)}
                                            disableWidthOnClick={true}
                                            disableDblClickPrevention={true}
                                            testId={`webhook-delete-${webhook.guid}`}
                                        />
                                    </>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
